import React, { useCallback, useRef, useEffect } from 'react'

import {
  selectSearchAiTagsSelector,
  selectSearchIsLoadingMenuSelector,
  selectSearchPrevStateSearchSelector,
  selectSearchResultsSelector,
  selectSearchStatusSelector,
  selectSearchTagsSelector,
  selectSearchTagsTypeSelector
} from 'features/common/model/selectors/searchSelectors'
import {
  clearPrevStateSearch,
  clearSearchResults,
  clearSearchState,
  removeSearchTag,
  revertPrevStateSearch,
  setIsLoading,
  setIsSearchInputActive,
  setIsSearchPage,
  setIsTagMenuVisible, setSearchInputValue,
  setSearchResultsSearchPage
} from 'features/common/model/slice/searchSlice'
import debounce from 'lodash/debounce'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { ROUTES } from 'shared/constants/routesPaths'
import { CloseOutlined, Input, SearchOutlined, TagOutlined } from 'shared/ui'
import styled from 'styled-components'
import { InputRef } from 'antd'

import {
  clearSearchParamsFromURL,
  buildSearchByNameUrl,
  buildSearchByTagsUrl, SEARCH_PARAMS,
} from '../../../../shared/utils/urlUtils'

import { TagInput } from './TagInput'
interface SearchInputFieldProps {
  isActive: boolean
  inputValue: string
  onSearch: (value: string) => void
  onClear: () => void
}

const SearchInputField = React.memo(({
  isActive,
  inputValue,
  onSearch,
  onClear,
}: SearchInputFieldProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const inputRef = useRef<InputRef>(null)

  const searchTags = useAppSelector(selectSearchTagsSelector)
  const searchAiTags = useAppSelector(selectSearchAiTagsSelector)
  const searchStatus = useAppSelector(selectSearchStatusSelector)
  const searchTagsType = useAppSelector(selectSearchTagsTypeSelector)
  const prevStateSearch = useAppSelector(selectSearchPrevStateSearchSelector)
  const searchResults = useAppSelector(selectSearchResultsSelector)
  const isLoadingMenu = useAppSelector(selectSearchIsLoadingMenuSelector)

  const [searchParams] = useSearchParams()
  const searchName = searchParams.get(SEARCH_PARAMS.SEARCH_NAME)

  const debouncedSearch = useRef(
    debounce((value: string) => {
      if (value.trim()) {
        onSearch(value)
      } else {
        onClear()
        dispatch(setIsSearchPage(false))
        clearSearchParamsFromURL()
      }
    }, 300)
  ).current

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    dispatch(setSearchInputValue(value))
    dispatch(setIsLoading(true))
    debouncedSearch(value)
  }, [dispatch, debouncedSearch])

  const handleClear = useCallback(() => {
    dispatch(setSearchInputValue(''))
    dispatch(clearSearchResults())
    dispatch(setIsSearchPage(false))
    clearSearchParamsFromURL()
    onClear()
  }, [dispatch, onClear])

  const handleOpenMenuTags = useCallback(() => {
    dispatch(setIsTagMenuVisible(true))
    if (inputRef.current) {
      inputRef.current.blur()
    }
  }, [dispatch, onClear])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim().length > 0 && !isLoadingMenu) {
      e.currentTarget.blur()
      navigate(buildSearchByNameUrl(inputValue))
      if (searchResults) {
        dispatch(setSearchResultsSearchPage(searchResults))
      }
      dispatch(setIsSearchPage(true))
      dispatch(setIsSearchInputActive(false))
      dispatch(setIsTagMenuVisible(false))
      dispatch(clearPrevStateSearch())
    }

    if (e.key === 'Escape') {
      if (prevStateSearch.searchAiTags.length > 0 || prevStateSearch.searchStatus.length > 0) {
        dispatch(revertPrevStateSearch())
        dispatch(setIsSearchInputActive(false))
        dispatch(clearSearchResults())
        dispatch(setSearchInputValue(''))
      } else {
        e.currentTarget.blur()
        dispatch(setSearchInputValue(''))
        dispatch(setIsSearchInputActive(false))
        dispatch(setIsTagMenuVisible(false))
        dispatch(clearSearchState())
      }
    }
  }, [inputValue, isLoadingMenu, searchResults, navigate, dispatch, prevStateSearch])

  const handleClickInputSearch = useCallback(() => {
    dispatch(setIsSearchInputActive(true))
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [dispatch])

  const handleFocusSearchInput = useCallback(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 100)
  }, [])

  const hasActiveTags = searchTags.length > 0 || searchAiTags.length > 0 || searchStatus.length > 0

  const handleRemoveTag = (tag: string) => {
    dispatch(removeSearchTag(tag))
  }

  // Update URL when tags change
  useEffect(() => {
    // Don't navigate if we're already on a board page
    if (location.pathname.startsWith(ROUTES.dashboard.board)) {
      return
    }

    if (searchTags.length > 0) {
      const tagIds = searchTags.map(tag => tag.id)
      navigate(buildSearchByTagsUrl(tagIds, searchTagsType))
    } else if (
      searchTags.length === 0 && !inputValue && !searchName && (searchAiTags.length === 0 && searchStatus.length === 0))
    {
      if (!location.pathname.startsWith(ROUTES.dashboard.allAssets) || location.search.includes('prv=')) {
        return
      }
      // If there are no tags, query, AI tags and statuses, clear URL on the all-assets page
      dispatch(clearSearchResults())
      navigate(buildSearchByNameUrl(''))
    }
  }, [searchTags, searchTagsType, searchAiTags, searchStatus, inputValue, searchName, navigate, location.pathname])

  return (
    <>
      {hasActiveTags ? (
        <TagInput
          isEditable={false}
          needComma={true}
          className="search-input-field-tag-input"
          isSearchInput={true}
          onFocusSearchInput={handleFocusSearchInput}
          currentTags={searchTags}
          onRemoveTag={handleRemoveTag}
        />
      ) : (
        <SInput
          ref={inputRef}
          placeholder="Search"
          prefix={<SearchOutlined />}
          suffix={(
            <SSuffixContainer>
              {inputValue && (
                <SClearIcon onClick={handleClear}>
                  <CloseOutlined />
                </SClearIcon>
              )}

              {isActive && (
                <>
                  {inputValue && <SDivider />}

                  <STagIcon onClick={handleOpenMenuTags}>
                    <TagOutlined />
                  </STagIcon>
                </>
              )}
            </SSuffixContainer>
          )}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={handleClickInputSearch}
        />
      )}
    </>
  )
})

const SInput = styled(Input)`
  margin-left: 10px;
`

const SSuffixContainer = styled.div`
  display: flex;
  align-items: center;
`

const SClearIcon = styled.span`
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  
  &:hover {
    color: rgba(0, 0, 0, 0.75);
  }
`

const SDivider = styled.div`
  height: 16px;
  width: 1px;
  background-color: #D9D9D9;
  margin: 0 8px;
`

const STagIcon = styled.span`
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  
  &:hover {
    color: rgba(0, 0, 0, 0.75);
  }
`

export default SearchInputField
